<template>
    <div class="zoho-user-mapping">
        <a-spin :spinning="loading">
            <a-table :dataSource="dataSource" :columns="columns">
                <span slot="zohoUser" slot-scope="text, record">
                    <span v-if="record.zohoUser">{{ record.zohoUser }}</span>
                    <a-icon v-else type="close" />
                </span>
                <span slot="mappingType" slot-scope="text, record">
                    <span v-if="record.zohoUser">Email</span>
                    <span v-else>-</span>
                </span>
                <span slot="zohoIntegration" slot-scope="text, record">
                    <a-button v-if="record.hasZohoChannel" type="danger" @click="removeZohoIntegration(record)">
                        {{ $t('message.removeZohoLink') }}
                    </a-button>
                    <a-button v-else type="primary" @click="connectToZoho(record)"> {{$t("message.connectToZoho")}} </a-button>
                </span>
            </a-table>
        </a-spin>
    </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapState } from "vuex";
import { ZOHO_API } from "../../../../api/zoho";
import { socketMessageStream } from "../../../../utils/socketStream";
import { first, map, timeout } from "rxjs/operators";
import CONFIG from "../../../../config/config";
import log2 from "../../../../utils/log2";

const columns = [
    {
        title: "Supsis Kullanıcısı",
        dataIndex: "supsisUser",
    },
    {
        title: "Zoho Kullanıcısı",
        dataIndex: "zohoUser",
        scopedSlots: { customRender: "zohoUser" },
    },
    {
        title: "Eşleşme Türü",
        dataIndex: "mappingType",
        scopedSlots: { customRender: "mappingType" },
    },
    {
        title: "Zoho Entegrasyonu",
        dataIndex: "zohoIntegration",
        scopedSlots: { customRender: "zohoIntegration" },
    },
];
const log = log2("ZohoUserMapping");

export default Vue.extend({
    name: "ZohoUserMapping",
    data() {
        return {
            columns,
            dataSource: [],
            loading: false,
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site._id,
            users: (state) => state.user.users.data,
            zohoChannels: (state) => state.channel.channels.filter((ch) => ch.type === "zoho" && !!ch.owner),
        }),
    },
    async created() {
        this.loading = true;

        try {
            await this.getUsers({ perPage: 1000, page: 1 });
            await this.listChannels();

            const zohoUsers = await this.fetchZohoUsers();

            const supsisUsers = _.cloneDeep(this.users);
            this.dataSource = supsisUsers.map((user) => ({
                supsisUser: user.email,
                zohoUser: zohoUsers.find((zu) => zu.email === user.email)?.email,
                hasZohoChannel: this.zohoChannels.some((ch) => ch.owner === user._id),
                ...user,
            }));
        } catch (error) {
            console.error("Veriler alınırken hata oluştu:", error);
        }

        this.loading = false;
    },
    methods: {
        ...mapActions({
            getUsers: "user/getUsers",
            listChannels: "channel/listChannels",
            createChannel: "channel/createChannel",
            deleteChannel: "channel/deleteChannel",
        }),
        async fetchZohoUsers() {
            try {
                return await ZOHO_API.listAllUsers();
            } catch (error) {
                console.error("Zoho kullanıcıları alınırken hata oluştu:", error);
                return [];
            }
        },
        async connectToZoho(record) {
            console.log("connectToZoho", record);
            this.loading = true;
            const redirectUrl = this.getRedirectURL();
            const state = `${this.siteId}_${redirectUrl}_${record._id}`;
            const CLIENT_ID = "1000.N2HT8IWMPR9WREGR8WUDIHOVAZ843A";

            const scopes = ["ZohoCRM.modules.contacts.ALL", "ZohoCRM.modules.deals.ALL", "ZohoCRM.settings.ALL"];

            const url = `https://accounts.zoho.com/oauth/v2/auth?scope=${scopes.join(
                ","
            )}&client_id=${CLIENT_ID}&response_type=code&access_type=offline&redirect_uri=${redirectUrl}&state=${state}`;

            this.listenSocket(record);
            this.window = window.open(url, "_blank");
        },
        listenSocket(record) {
            socketMessageStream
                .pipe(
                    first((x) => x.cmd === "zoho_callback_response"),
                    map((x) => x.data),
                    timeout(30000)
                )
                .toPromise()
                .then(async (data) => {
                    this.window.close();
                    console.log("Zoho callback response", data, !data?.success);
                    const payload = data;
                    if (!data?.success) {
                        this.loading = false;
                        this.$message.error("Failed to connect Zoho CRM");
                        return;
                    }
                    await this.createZohoChannel(record, payload);
                    this.loading = false;
                    this.$message.success("Zoho CRM connected successfully");
                })
                .catch((err) => {
                    this.window.close();
                    this.loading = false;
                    this.$message.error("Failed to connect Zoho CRM");
                });
        },
        async createZohoChannel(record, payload) {
            console.log("createZohoChannel", { record, payload });
            const data = {
                payload,
                type: "zoho",
                name: `${record.name}'s Zoho`,
                owner: record._id,
            };

            try {
                await this.createChannel(data);
                this.$message.success("Zoho channel created successfully");
                await this.listChannels(); // Refresh channels
            } catch (err) {
                this.$message.error("Failed to create Zoho channel");
            }
        },
        async removeZohoIntegration(record) {
            const channelId = this.zohoChannels.find((ch) => ch.owner === record._id)?._id;
            if (channelId) {
                try {
                    await this.deleteChannel(channelId);
                    this.$message.success("Zoho integration removed successfully");
                    await this.listChannels(); // Refresh channels
                } catch (err) {
                    this.$message.error("Failed to remove Zoho integration");
                }
            }
        },
        getRedirectURL() {
            return `${CONFIG.SOCKET_URL}/webhook/oauth2/zoho`;
        },
    },
});
</script>

<style scoped lang="scss"></style>
